import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing['2xl']};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 26px 26px;
    opacity: 0.4;
    mask-image: radial-gradient(circle at center, rgba(0, 0, 0, 0.95), transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 15% 30%, rgba(45, 138, 91, 0.22), transparent 28%),
      radial-gradient(circle at 85% 20%, rgba(193, 154, 91, 0.18), transparent 28%),
      radial-gradient(circle at 70% 80%, rgba(58, 180, 137, 0.18), transparent 30%);
    filter: blur(20px);
    opacity: 0.8;
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.lg};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
  }
`;

const Shell = styled.div`
  width: min(1160px, 100%);
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: ${theme.spacing['2xl']};
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const InfoPanel = styled.div`
  background: linear-gradient(145deg, rgba(9, 26, 19, 0.95), rgba(6, 18, 14, 0.96));
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.02);
  border-radius: ${theme.borderRadius['3xl']};
  padding: ${theme.spacing['2xl']};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: -40%;
    background: radial-gradient(circle at 30% 30%, rgba(45, 138, 91, 0.18), transparent 45%),
      radial-gradient(circle at 70% 60%, rgba(193, 154, 91, 0.18), transparent 50%);
    filter: blur(18px);
    opacity: 0.9;
    pointer-events: none;
  }
`;

const InfoContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const Badge = styled.span`
  align-self: flex-start;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.08);
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.xs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  font-family: ${theme.fonts.heading};
  font-weight: ${theme.fontWeights.bold};
  font-size: clamp(${theme.fontSizes['2xl']}, 3vw, ${theme.fontSizes['4xl']});
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.md};
  line-height: 1.6;
  max-width: 34ch;
`;

const ModuleGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.md};
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const ModuleCard = styled.div`
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.xl};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const ModuleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
`;

const ModuleBadge = styled.span`
  width: 28px;
  height: 28px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(45, 138, 91, 0.18);
  border: 1px solid rgba(45, 138, 91, 0.4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.fontWeights.semibold};
`;

const ModuleTitle = styled.div`
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
`;

const ModuleText = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
  margin: 0;
`;

const Card = styled.div`
  background: linear-gradient(170deg, rgba(9, 24, 18, 0.95), rgba(6, 16, 12, 0.96));
  border-radius: ${theme.borderRadius['3xl']};
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.03);
  padding: ${theme.spacing['2xl']};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  width: 100%;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.xl};
  }
  
  @media (min-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing['3xl']};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  margin-bottom: 0;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const LogoIcon = styled.div`
  width: 52px;
  height: 52px;
  background: conic-gradient(from 180deg at 50% 50%, rgba(45, 138, 91, 0.85) 0deg, rgba(193, 154, 91, 0.9) 140deg, rgba(45, 138, 91, 0.9) 260deg, rgba(45, 138, 91, 0.85) 360deg);
  border-radius: ${theme.borderRadius['2xl']};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textPrimary};
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  box-shadow: 0 10px 40px rgba(45, 138, 91, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.16);
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Brand = styled.h1`
  font-size: ${theme.fontSizes['xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const BrandSub = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Pill = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.textPrimary};
  background: rgba(45, 138, 91, 0.18);
  border: 1px solid rgba(45, 138, 91, 0.4);
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  letter-spacing: 0.04em;
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.textSecondary};
  margin: 0;
`;

interface LayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

export const Layout: React.FC<LayoutProps> = ({ children, title, subtitle }) => {
  return (
    <LayoutContainer>
      <Shell>
        <InfoPanel className="slide-in">
          <InfoContent>
            <Badge>生态保护修复业务体系</Badge>
            <HeroTitle>自然资源部生态保护修复一体化平台</HeroTitle>
            <HeroSubtitle>
              贯通监测、预警、规划与项目管理，形成“监测-研判-实施-反馈”闭环，支撑生态治理全链路协同。
            </HeroSubtitle>
            <ModuleGrid>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>A</ModuleBadge>
                  <ModuleTitle>自然生态监测评价</ModuleTitle>
                </ModuleHeader>
                <ModuleText>构建多源数据监测体系，支撑生态质量基线与动态评估。</ModuleText>
              </ModuleCard>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>B</ModuleBadge>
                  <ModuleTitle>生态风险监测预警</ModuleTitle>
                </ModuleHeader>
                <ModuleText>聚焦风险源识别与趋势研判，形成智能预警闭环。</ModuleText>
              </ModuleCard>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>C</ModuleBadge>
                  <ModuleTitle>生态修复规划实施监测评价</ModuleTitle>
                </ModuleHeader>
                <ModuleText>对修复规划落地过程进行全周期监测与成效评估。</ModuleText>
              </ModuleCard>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>D</ModuleBadge>
                  <ModuleTitle>生态保护修复项目监测预警</ModuleTitle>
                </ModuleHeader>
                <ModuleText>强化项目过程监管与异常预警，保障工程成效。</ModuleText>
              </ModuleCard>
            </ModuleGrid>
          </InfoContent>
        </InfoPanel>

        <Card className="fade-in">
          <Header>
            <Logo>
              <LogoIcon>NR</LogoIcon>
              <LogoText>
                <Brand>自然生态修复平台</Brand>
                <BrandSub>生态保护修复一体化</BrandSub>
              </LogoText>
            </Logo>
            <Pill>生态修复协同</Pill>
          </Header>
          <div>
            <Title>{title}</Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </div>
          {children}
        </Card>
      </Shell>
    </LayoutContainer>
  );
};
